import { useEffect, useMemo, useRef, useState } from 'react'
import type { ContentSectionKey } from '@/content'
import {
  defaultContent,
  resetSection,
  saveSection,
  useContent,
  useContentRefresh,
} from '@/content'
import { Icon } from '@/components/ui'
import { cn } from '@/lib/cn'
import { inferSection } from './fields/inferFields'
import { ListEditor } from './fields/ListEditor'
import { RecordForm } from './fields/RecordForm'
import { TagsInput } from './fields/TagsInput'
import { suggestionsFor } from './fields/vocabulary'
import styles from './AdminDashboard.module.css'

interface SectionEditorProps {
  sectionKey: ContentSectionKey
  label: string
  /** The value currently live on the site. */
  live: unknown
  token: string
  onDirtyChange: (dirty: boolean) => void
}

type Status =
  | { kind: 'idle' }
  | { kind: 'saving' }
  /** `removed`: Cloudinary assets this save left with no references. */
  | { kind: 'saved'; removed: string[] }
  | { kind: 'error'; message: string }

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}

/**
 * Edits one section, and saves only that section.
 *
 * ## Why per-section
 *
 * The previous panel held the whole document in one draft and wrote all of
 * it on every save. That meant editing a project could overwrite an
 * unrelated change made in another tab, and a mistake anywhere was a
 * mistake everywhere. Here the unit of editing and the unit of saving are
 * the same thing, which is the only arrangement where "I broke the
 * projects" cannot also mean "and the contact details".
 *
 * ## Going live
 *
 * A successful save refetches the content the whole site reads from, so the
 * change is visible immediately rather than after a reload.
 *
 * ## Reset
 *
 * Drops the stored override so the section falls back to the value compiled
 * into the site. Those defaults are in version control, so this is always
 * available and never loses anything that was not typed here.
 *
 * ## Media
 *
 * Saving also tidies Cloudinary: a picture this section no longer points at
 * is moved to a `trash/` folder, so replacing a project cover does not leave
 * the old one behind forever. The panel says when that happened rather than
 * doing it quietly, and the file stays recoverable — nothing here destroys
 * anything.
 */
export const SectionEditor = ({
  sectionKey,
  label,
  live,
  token,
  onDirtyChange,
}: SectionEditorProps) => {
  const refresh = useContentRefresh()
  const content = useContent() as unknown as Record<string, unknown>
  const [draft, setDraft] = useState<unknown>(() => structuredClone(live))
  const [status, setStatus] = useState<Status>({ kind: 'idle' })
  const [confirmReset, setConfirmReset] = useState(false)

  /**
   * Set when this editor is the reason `live` is about to change.
   *
   * A successful save refetches the content, which changes `live`, which
   * re-seeds the draft below — and that was wiping the "Live" confirmation
   * in the same frame it appeared, so a save looked like it had done
   * nothing. The flag lets the re-seed keep the result it caused.
   */
  const justSaved = useRef(false)

  // Re-seed when the section changes, or when a save brings new live values.
  useEffect(() => {
    setDraft(structuredClone(live))
    setConfirmReset(false)
    if (justSaved.current) {
      justSaved.current = false
      return
    }
    setStatus({ kind: 'idle' })
  }, [sectionKey, live])

  const shape = useMemo(
    () =>
      inferSection(
        draft,
        (defaultContent as unknown as Record<string, unknown>)[sectionKey],
        sectionKey,
      ),
    [draft, sectionKey],
  )

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(live),
    [draft, live],
  )

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange])

  const save = async () => {
    setStatus({ kind: 'saving' })
    try {
      const { removed } = await saveSection(token, sectionKey, draft)
      justSaved.current = true
      await refresh()
      setStatus({ kind: 'saved', removed })
    } catch (cause) {
      setStatus({
        kind: 'error',
        message: cause instanceof Error ? cause.message : 'Save failed',
      })
    }
  }

  const reset = async () => {
    setStatus({ kind: 'saving' })
    try {
      const { removed } = await resetSection(token, sectionKey)
      justSaved.current = true
      await refresh()
      setConfirmReset(false)
      setStatus({ kind: 'saved', removed })
    } catch (cause) {
      setStatus({
        kind: 'error',
        message: cause instanceof Error ? cause.message : 'Reset failed',
      })
    }
  }

  return (
    <section className={styles.editor}>
      <header className={styles.editorBar}>
        <div>
          <h1 className="t-22">{label}</h1>
          <p className={`t-12 ${styles.editorHint}`}>
            {shape.kind === 'list' || shape.kind === 'tags'
              ? `${Array.isArray(draft) ? draft.length : 0} entries · saved on its own, nothing else is touched`
              : 'Saved on its own, nothing else is touched'}
          </p>
        </div>

        <div className={styles.editorActions}>
          {status.kind === 'error' && (
            <span className={cn(styles.pill, styles.pillError)} role="alert">
              {status.message}
            </span>
          )}
          {status.kind === 'saved' && !dirty && (
            <span
              className={cn(styles.pill, styles.pillOk)}
              role="status"
              title={
                status.removed.length > 0
                  ? `Moved to the Cloudinary trash folder, recoverable:\n${status.removed.join('\n')}`
                  : undefined
              }
            >
              {status.removed.length > 0
                ? `Live · ${status.removed.length} file${status.removed.length > 1 ? 's' : ''} to trash`
                : 'Live'}
            </span>
          )}
          {dirty && status.kind !== 'saving' && (
            <span className={styles.pill}>Unsaved</span>
          )}

          <button
            type="button"
            onClick={() => setDraft(structuredClone(live))}
            disabled={!dirty || status.kind === 'saving'}
            className={styles.ghostButton}
          >
            Discard
          </button>

          {confirmReset ? (
            <>
              <button
                type="button"
                onClick={() => void reset()}
                className={cn(styles.ghostButton, styles.dangerButton)}
              >
                Reset to default
              </button>
              <button
                type="button"
                onClick={() => setConfirmReset(false)}
                className={styles.ghostButton}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmReset(true)}
              disabled={status.kind === 'saving'}
              className={styles.ghostButton}
              title="Discard the stored version and fall back to the one built into the site"
            >
              <Icon name="arrowCounterClockwise" size={14} />
              Reset
            </button>
          )}

          <button
            type="button"
            onClick={() => void save()}
            disabled={!dirty || status.kind === 'saving'}
            className={styles.saveButton}
          >
            <Icon name="floppyDisk" size={14} />
            {status.kind === 'saving' ? 'Saving…' : 'Save'}
          </button>
        </div>
      </header>

      <div className={styles.editorBody}>
        {/*
          * One column, capped. The fields inside already wrap to the width
          * they are given, and given the whole of a wide screen they wrap to
          * four columns of short inputs with a paragraph stretched across
          * all of it — readable at neither end.
          */}
        <div className={styles.editorColumn}>
          {shape.kind === 'list' && (
            <ListEditor
              fields={shape.fields}
              template={shape.template}
              token={token}
              path={sectionKey}
              items={Array.isArray(draft) ? draft : []}
              onChange={setDraft}
            />
          )}

          {shape.kind === 'tags' && (
            <div className={styles.panel}>
              <TagsInput
                id={sectionKey}
                value={Array.isArray(draft) ? (draft as string[]) : []}
                suggestions={suggestionsFor(sectionKey, content)}
                onChange={(next) => setDraft(next)}
              />
            </div>
          )}

          {shape.kind === 'text' && (
            <div className={styles.panel}>
              <textarea
                id={sectionKey}
                value={typeof draft === 'string' ? draft : ''}
                onChange={(event) => setDraft(event.target.value)}
                className={styles.prose}
                rows={6}
              />
            </div>
          )}

          {shape.kind === 'record' && (
            <div className={styles.panel}>
              <RecordForm
                fields={shape.fields}
                value={asRecord(draft)}
                token={token}
                path={sectionKey}
                onChange={setDraft}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
